import _ from 'lodash';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Panel from 'src/components/common/Panel';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Vendor from 'src/models/AP/Vendor';

export interface IVendorSearchCriteria {
    vendorNumber?: string;
}

interface IVendorSearchProps {
    vendors?: PagedResultSet<Vendor>;
    onVendorSearchChange: (term: string) => void;
    onSearch: (criteria: IVendorSearchCriteria) => void;
    // Opens the same FindVendorDialog the toolbar's own "Find" button uses (VendorsPage.tsx's
    // findVendor()) - this is a second entry point to it, not a replacement.
    onAdvancedFind: () => void;
}

interface IOptionType {
    value: string;
    label: string;
}

// Same Vendor No. lookahead + Search/Expand-Minimize/Clear button set as VoucherSearch.tsx - starts
// with just the vendor lookup since that's the only cross-field search VendorsPage needs today, but
// keeps the same collapsible-row shape so more criteria can be added later without restructuring.
const VendorSearch = (props: IVendorSearchProps) => {
    const { control, getValues, reset } = useForm<IVendorSearchCriteria>();
    const [isSearchingVendors, setIsSearchingVendors] = useState<boolean>(false);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

    const vendorOptions: Array<IOptionType> = (props.vendors?.items || []).map(v => ({
        value: `${v.vendorNumber}`,
        label: `${v.vendorName} (${v.vendorNumber})`
    }));

    const vendorSearch = _.debounce((inputText: string) => {
        if (!!inputText && inputText.length > 0) {
            props.onVendorSearchChange(inputText);
        }
    }, 500);

    const handleVendorSearchChanged = (inputText: string) => {
        vendorSearch(inputText);
    };

    const handleSearch = () => {
        const data = getValues();
        props.onSearch({
            vendorNumber: data.vendorNumber
        });
    };

    const handleClear = () => {
        reset();
        props.onSearch({});
    };

    return (
        <Panel backgroundColor="#D3D3D3">
            <Panel panelTitle="Find Vendors">
                <div style={{ padding: '0 .5rem', marginTop: '-1rem' }}>
                    <div className="form-row">
                        {!isCollapsed && <div style={{ width: '16%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Vendor No.</label>
                            <Controller
                                name="vendorNumber"
                                control={control}
                                defaultValue={null}
                                render={({ onChange, value }) => (
                                    <Select
                                        placeholder="Select Vendor"
                                        options={vendorOptions}
                                        value={value ? (vendorOptions.find(o => o.value === value) ?? null) : null}
                                        onMenuOpen={() => setIsSearchingVendors(true)}
                                        onMenuClose={() => setIsSearchingVendors(false)}
                                        onBlur={() => setIsSearchingVendors(false)}
                                        menuIsOpen={isSearchingVendors}
                                        isSearchable={true}
                                        isClearable={true}
                                        onInputChange={handleVendorSearchChanged}
                                        onChange={(opt: IOptionType | null) => onChange(opt?.value ?? null)}
                                    />
                                )}
                            />
                        </div>}
                        <div style={{ display: 'flex', marginLeft: 'auto', alignItems: 'center' }}>
                            <Button
                                text={'Search'}
                                className="blue-hover-button"
                                style={{ width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                                onClick={() => handleSearch()}
                            />
                            <Button
                                text="Advanced Find"
                                className="blue-hover-button"
                                style={{ whiteSpace: 'nowrap', width: 'auto', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                                onClick={() => props.onAdvancedFind()}
                            />
                            <Button
                                text={isCollapsed ? '(+) Expand' : '(-) Minimize'}
                                className="blue-hover-button"
                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                onClick={() => setIsCollapsed(!isCollapsed)}
                            />
                            <Button
                                text="Clear"
                                className="blue-hover-button"
                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                onClick={() => handleClear()}
                            />
                        </div>
                    </div>
                </div>
            </Panel>
        </Panel>
    );
};

export default VendorSearch;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/VendorSearch.tsx